import React from 'react';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useWindowWidth } from '../../helpers';

import Breadcrumbs from "../components/Breadcrumbs";
import Loader from '../../components/Loader';

import { styled } from "../../stitches.config";
import { h2, h3, body, h4Strong } from '../commonStyles/Fonts';

import Arrow from "../assets/icons/arrow-up.svg";
import Close from "../assets/icons/close-podcast.svg";
import Footer from "../components/Footer"
import Error from 'src/components/Error';

type DataType = {
    categoryId: string,
    categoryName: string,
    categoryDates: {
        date: string,
        dateString: string,
    }[]
}[]

const PostsMapPage = () => {
    if (window.APP_SETTINGS.response_code != 200) {
        return <Error error={"Page not found"} />;
    }

    const [data, setData] = useState<DataType>([])
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [isMenuOpen, setMenuOpen] = useState(false);
    const toggleMenu = () => {
        setMenuOpen(!isMenuOpen);
    };

    const isDesktop = useWindowWidth() > 996;

    const fetchData = async () => {
        const fetchUrl = `${window.API_HOST}/api/posts-map`;
        try {
            const res = await axios.get(fetchUrl);
            setData(res.data);
            setIsLoading(false);
        } catch (err) {
            console.log(err)
        }
    }

    const sectionRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        (sectionRef.current?.children[0] as HTMLElement)?.focus();
    });

    useEffect(() => {
        fetchData()
    }, [])

    const scrollToSection = (i: number) => {
        sectionRef.current?.children[i].scrollIntoView({
            behavior: 'smooth'
        })
    }

    if (isLoading) {
        return (
            <Loader />
        )
    } else {
        return (
            <>
                <WrapBizon>
                    <div className="container">
                        <Breadcrumbs category={{
                            name: `Mapa artykułów`, link: `/posts-map`
                        }} invert />
                        <FlexWrapper>
                            {data && data.length > 0 ?
                                <LeftColumn className={body()}>
                                    <LeftColumnMobileMenu onClick={toggleMenu}>
                                        <p className={h4Strong()}><span></span>Przejdź do sekcji...</p>
                                        {isMenuOpen ?
                                            <div className="close"></div>
                                            :
                                            <div className="arrow"></div>
                                        }
                                    </LeftColumnMobileMenu>
                                    <LeftColumnContent onClick={toggleMenu} className={!isDesktop && isMenuOpen ? "open" : ""}>
                                        {data.map((categories, i) => <p onClick={() => { scrollToSection(i) }}>{categories.categoryName}</p>)}
                                    </LeftColumnContent>
                                </LeftColumn>
                                :
                                null
                            }
                            <RightColumn>
                                <h2 className={h2()}>Mapa artykułów</h2>
                                <div ref={sectionRef}>
                                    {data?.map(({ categoryName, categoryDates, categoryId }) => {
                                        return (
                                            <div>
                                                <h3 className={h3()}>
                                                    {categoryName}
                                                </h3>
                                                <p>
                                                    {categoryDates.map((item) => (
                                                        <><Link to={`/posts-map/${categoryId},${item.date}`}>{item.dateString}</Link>, </>
                                                    ))}
                                                </p>
                                            </div>
                                        )
                                    })}
                                </div>
                            </RightColumn>
                        </FlexWrapper>
                    </div>
                </WrapBizon>
                <Footer />
            </>
        )
    }
}

export default PostsMapPage;

const WrapBizon = styled("div", {
    background: "$grayscale10",
    padding: "64px 0 0 0",
    position: "relative",
    "@bp4": {
        padding: "40px 0 0 0",
    },
    "& .breadcrumbs": {
        "& ul": {
            "& li:nth-child(2)": {
                textTransform: "capitalize",
            },
        },
    }
});

const FlexWrapper = styled("div", {
    marginTop: "24px",
    "@bp4": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        maxWidth: "1164px",
    },
});

const LeftColumnMobileMenu = styled("div", {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "100%",
    background: "$grayscale0",
    color: "$grayscale100",
    padding: "0 20px",
    borderBottom: "1px solid $grayscale25",
    "@bp4": {
        display: "none",
    },
    "& p": {
        position: "relative",
        paddingLeft: "16px",
        "&:before": {
            content: "",
            position: "absolute",
            display: "block",
            left: 0,
            top: "6px",
            width: "6px",
            height: "6px",
            backgroundColor: "$primary",
            borderRadius: "6px",
        },
        "&:after": {
            content: "",
            position: "absolute",
            display: "block",
            top: "14px",
            left: 0,
            width: "6px",
            height: "6px",
            backgroundColor: "$primary",
            borderRadius: "6px",
        },
    }
});

const LeftColumnContent = styled("div", {
    padding: "0px",
    width: "100%",
    overflow: "hidden",
    height: "0px",
    "@bp4": {
        height: "auto",
        width: "260px",
        padding: "32px 40px 24px 40px",
        flexShrink: 0,
        minHeight: "20px",
    },
    "p": {
        color: "$grayscale100",
        display: "block",
        marginBottom: "8px",
        cursor: 'pointer',
    },
});

const LeftColumn = styled("div", {
    width: "100%",
    background: "$grayscale0",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    "@bp4": {
        width: "260px",
        position: "static",
        marginRight: "20px",
    },
    "@bp5": {
        width: "300px",
    },
    "& .open": {
        height: "auto",
        padding: "16px 16px 8px 16px",
    },
    "& .close": {
        width: "12px",
        height: "12px",
        backgroundImage: `url("${Close}")`,
        backgroundPosition: "center",
        backgroundSize: "cover",
    },
    "& .arrow": {
        width: "14px",
        height: "8px",
        backgroundImage: `url("${Arrow}")`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        transform: "rotate(90deg)"
    },
});

const RightColumn = styled("div", {
    marginTop: "30px",
    marginBottom: "100px",
    color: "$grayscale100",
    width: "100%",
    "@bp4": {
        fontSize: "24px",
        lineHeight: "30px",
        maxWidth: "732px",
    },
    "& ol": {
        paddingLeft: "16px",
        "@bp4": {
            fontSize: "24px",
            lineHeight: "30px",
            paddingLeft: "24px",
        },

        "& li": {
            marginBottom: "15px",
        }
    },

    "& a": {
        color: "$grayscale100",
        fontFamily: '$charis',
        "&:hover": {
            color: "$grayscale100",
        }
    },

    "& .centered": {
        textAlign: "left",
        position: "relative",
        padding: "32px 0 16px 0",
        "@bp4": {
            padding: "56px 0 16px 0",
        },
        "&:before": {
            content: "",
            position: "absolute",
            top: 0,
            left: 0,
            width: "54px",
            height: "7px",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "@bp4": {
                width: "76px",
                height: "10px",
            }
        }
    },
});
